import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles, AlertTriangle, DollarSign, FileWarning, Workflow, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const SEVERITY = {
  high: { color: "text-red-300 border-red-500/30 bg-red-500/5", icon: AlertTriangle },
  medium: { color: "text-amber-300 border-amber-500/30 bg-amber-500/5", icon: FileWarning },
  low: { color: "text-blue-300 border-blue-500/30 bg-blue-500/5", icon: Workflow },
};

const CATEGORY_ICON = {
  missed_revenue: DollarSign,
  documentation: FileWarning,
  denial_risk: AlertTriangle,
  workflow: Workflow,
};

export default function AIRecommendations({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/ai-ops/recommendations/${jobId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [jobId]);

  if (loading) return (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 flex items-center gap-3">
      <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
      <span className="text-xs text-slate-500">Analyzing job for missed revenue & risks…</span>
    </div>
  );
  if (!data || data.recommendations.length === 0) return (
    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5 flex items-center gap-3" data-testid="ai-recs-empty">
      <Sparkles className="w-4 h-4 text-emerald-400" />
      <div>
        <div className="text-sm font-semibold text-emerald-300">Job looks complete</div>
        <div className="text-xs text-slate-400">No missed operations or risk flags detected by AI.</div>
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-5" data-testid="ai-recs-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-400" />
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-purple-300">AI Operational Recommendations</div>
        </div>
        {data.missed_revenue_total > 0 && (
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30">
            ${data.missed_revenue_total.toLocaleString()} missed
          </Badge>
        )}
      </div>
      <div className="space-y-3">
        {data.recommendations.map((r, idx) => {
          const sev = SEVERITY[r.severity];
          const CatIcon = CATEGORY_ICON[r.category] || Sparkles;
          const Icon = sev.icon;
          return (
            <div key={idx} data-testid={`ai-rec-${idx}`} className={`rounded-lg border ${sev.color} p-3`}>
              <div className="flex items-start gap-3">
                <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-sm font-semibold">{r.title}</div>
                    {r.amount_impact > 0 && <Badge className="bg-slate-800 border-slate-700 text-slate-300 font-mono text-[10px]">+${r.amount_impact}</Badge>}
                    <Badge className="bg-slate-900/60 border-slate-700 text-slate-400 text-[10px] uppercase tracking-wide ml-auto"><CatIcon className="w-2.5 h-2.5 mr-1" />{r.category.replace("_", " ")}</Badge>
                  </div>
                  <div className="text-xs text-slate-400">{r.description}</div>
                  <div className="text-xs text-slate-300 mt-1.5 pt-1.5 border-t border-slate-800/40">
                    <strong className="text-slate-200">→ Action:</strong> {r.action}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {data.carrier_pattern && (
        <div className="mt-4 pt-3 border-t border-purple-500/20">
          <div className="text-[10px] uppercase tracking-wider font-bold text-purple-300 mb-1">Carrier Behavior</div>
          <div className="text-xs text-slate-400">{data.carrier_pattern.best_argument}</div>
        </div>
      )}
    </div>
  );
}
